import { useMemo, useState } from "react";
import {
  Card,
  Box,
  Stack,
  Grid,
  TextField,
  Button,
  Alert,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table as MuiTable,
  TableBody as MuiTableBody,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { BaseTable } from "../BaseTable/BaseTable";
import { Loader } from "../Loader";
import { fetchApi, fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import {
  paymentGatewayTransactionStatusUrl,
  paymentGatewayTransactionRefundUrl,
} from "src/react-query/endPoints";
import { changeToCSV, downloadCSV } from "src/utils/convertAnddownloadCsv";
import { AMIK_LOGO_URL, loadImageDataUrl } from "src/utils/downloadQr";
import {
  FIELD_LABELS,
  FIELD_ORDER,
  resultStatusInfo,
  refundStatusLabel,
  formatAmount,
} from "./paytmStatusMaps";

const toYMD = (date) => date.toISOString().slice(0, 10);
const DEFAULT_TO = toYMD(new Date());
const DEFAULT_FROM = toYMD(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));

const fmtDateTime = (value) => (value ? new Date(value).toLocaleString() : "-");

const columns = [
  {
    field: "created_at",
    headerName: "Date",
    minWidth: 170,
    flex: 1,
    renderCell: ({ value }) => fmtDateTime(value),
  },
  { field: "id", headerName: "Payment ID", minWidth: 170, flex: 1 },
  {
    field: "amount",
    headerName: "Amount",
    minWidth: 110,
    renderCell: ({ row }) =>
      row.amount != null ? `${row.amount} ${row.currency || ""}`.trim() : "-",
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 110,
    renderCell: ({ value }) => (
      <Chip
        size="small"
        label={value || "-"}
        color={value === "captured" || value === "authorized" ? "success" : "default"}
        variant="outlined"
      />
    ),
  },
  { field: "method", headerName: "Method", minWidth: 100, valueFormatter: ({ value }) => value || "-" },
  { field: "order_id", headerName: "Order ID", minWidth: 160, valueFormatter: ({ value }) => value || "-" },
  { field: "email", headerName: "Email", minWidth: 180, flex: 1, valueFormatter: ({ value }) => value || "-" },
  { field: "contact", headerName: "Contact", minWidth: 130, valueFormatter: ({ value }) => value || "-" },
  { field: "charger_id", headerName: "Charger", minWidth: 120, valueFormatter: ({ value }) => value || "-" },
  { field: "connector_id", headerName: "Conn.", minWidth: 80, valueFormatter: ({ value }) => value || "-" },
  {
    field: "refund_status",
    headerName: "Refund",
    minWidth: 120,
    // Show a human-readable label instead of a raw Paytm code (e.g. 601).
    renderCell: ({ value }) => refundStatusLabel(value) || "-",
  },
  { field: "fee", headerName: "Fee", minWidth: 90, valueFormatter: ({ value }) => (value ?? "-") },
  { field: "captured", headerName: "Captured", minWidth: 100, valueFormatter: ({ value }) => value || "-" },
];

// Build the readable [label, value] rows from a Paytm status body.
const buildReadableRows = (body) => {
  if (!body) return [];
  return FIELD_ORDER.filter((k) => body[k] !== undefined && body[k] !== null && `${body[k]}` !== "").map(
    (k) => {
      let value = body[k];
      if (k === "txnAmount" || k === "refundAmt") value = formatAmount(value);
      return [FIELD_LABELS[k] || k, `${value}`];
    }
  );
};

export const PaymentGatewayTransactions = ({ id }) => {
  const [inputs, setInputs] = useState({ from: DEFAULT_FROM, to: DEFAULT_TO });
  const [applied, setApplied] = useState({ from: DEFAULT_FROM, to: DEFAULT_TO });

  const params = useMemo(() => ({ from: applied.from, to: applied.to }), [applied]);

  const { data, isFetching, refetch } = useQuery({
    queryKey: queryKeys.paymentGatewayTransactions(id, params),
    queryFn: fetchWithError,
    enabled: Boolean(id),
    keepPreviousData: true,
  });

  const supported = data?.supported !== false;
  const canCheckLiveStatus = Boolean(data?.canCheckLiveStatus);
  const rows = useMemo(
    () => (data?.data ?? []).map((t, i) => ({ rowId: t.id || i, ...t })),
    [data]
  );

  const [statusDialog, setStatusDialog] = useState({
    open: false,
    loading: false,
    orderId: null,
    data: null,
    refund: null,
    upiId: null,
    error: null,
  });
  const [refund, setRefund] = useState({
    amount: "",
    reason: "",
    submitting: false,
    message: null,
    error: null,
  });

  const fetchStatus = async (orderId) => {
    setStatusDialog({
      open: true,
      loading: true,
      orderId,
      data: null,
      refund: null,
      upiId: null,
      error: null,
    });
    setRefund({ amount: "", reason: "", submitting: false, message: null, error: null });
    try {
      const resp = await fetchApi({
        url: paymentGatewayTransactionStatusUrl(id, orderId),
        method: "GET",
      });
      const body = resp?.data || null;
      setStatusDialog({
        open: true,
        loading: false,
        orderId,
        data: body,
        refund: resp?.refund || null,
        upiId: resp?.upiId || null,
        error: resp?.success === false ? resp?.message : null,
      });
      // Pre-fill refund amount with the txn amount as the cap/default.
      setRefund((r) => ({ ...r, amount: body?.txnAmount ? `${body.txnAmount}` : "" }));
      // If the live status differed and the backend updated the DB, refresh the
      // list so the table (status / refund column) reflects the latest values.
      if (resp?.dbUpdated) refetch();
    } catch (e) {
      setStatusDialog({
        open: true,
        loading: false,
        orderId,
        data: null,
        error: e?.response?.data?.message || e?.message || "Failed to fetch status",
      });
    }
  };

  const statusBody = statusDialog.data;
  const refundInfo = statusDialog.refund;
  const txnAmount = statusBody?.txnAmount != null ? Number(statusBody.txnAmount) : null;
  // A refund is considered "recorded" if we have a ref id, a status, or a > 0 amount.
  const hasRefund = Boolean(
    refundInfo &&
      (refundInfo.refId ||
        (refundInfo.status !== null && refundInfo.status !== undefined && `${refundInfo.status}` !== "") ||
        Number(refundInfo.amount) > 0)
  );
  // Offer a manual refund only when nothing has been refunded yet.
  const canRefund = canCheckLiveStatus && statusBody && !hasRefund;
  const statusInfo = resultStatusInfo(statusBody?.resultInfo?.resultStatus);

  const handleRefund = async () => {
    const amt = Number(refund.amount);
    if (!amt || amt <= 0) {
      setRefund((r) => ({ ...r, error: "Enter a refund amount greater than 0.", message: null }));
      return;
    }
    if (txnAmount != null && amt > txnAmount) {
      setRefund((r) => ({
        ...r,
        error: `Refund amount cannot be greater than the transaction amount (${txnAmount}).`,
        message: null,
      }));
      return;
    }
    setRefund((r) => ({ ...r, submitting: true, error: null, message: null }));
    try {
      const resp = await fetchApi({
        url: paymentGatewayTransactionRefundUrl(id, statusDialog.orderId),
        method: "POST",
        body: { amount: amt, reason: refund.reason || undefined },
      });
      setRefund((r) => ({
        ...r,
        submitting: false,
        message: resp?.success ? resp?.message : null,
        error: resp?.success ? null : resp?.message || "Refund failed",
      }));
      if (resp?.success) {
        fetchStatus(statusDialog.orderId); // refresh status after refund
      }
    } catch (e) {
      setRefund((r) => ({
        ...r,
        submitting: false,
        error: e?.response?.data?.message || e?.message || "Refund failed",
      }));
    }
  };

  const handleDownloadPdf = async () => {
    if (!statusBody) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 14;
    const PRIMARY = [11, 79, 138];
    const MUTED = [107, 114, 128];
    const DARK = [31, 41, 55];
    const STATUS_RGB = {
      success: [46, 125, 50],
      warning: [239, 108, 0],
      error: [198, 40, 40],
      default: [107, 114, 128],
    };
    const sc = STATUS_RGB[statusInfo.color] || STATUS_RGB.default;

    // ---- Header: logo (left) + company block (right) ----
    const logo = await loadImageDataUrl(AMIK_LOGO_URL);
    let headerBottom = 12;
    if (logo?.dataUrl) {
      const logoW = 34;
      const logoH = logo.width ? (logoW * logo.height) / logo.width : 12;
      doc.addImage(logo.dataUrl, "JPEG", marginX, 10, logoW, logoH);
      headerBottom = 10 + logoH;
    }
    doc.setFont("helvetica", "bold").setFontSize(13).setTextColor(...PRIMARY);
    doc.text("AMIK Technologies", pageWidth - marginX, 15, { align: "right" });
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED);
    doc.text("info@amiktech.in", pageWidth - marginX, 21, { align: "right" });
    doc.text("www.amiktech.in", pageWidth - marginX, 26, { align: "right" });

    let y = Math.max(headerBottom, 28) + 6;
    doc.setDrawColor(...PRIMARY).setLineWidth(0.6);
    doc.line(marginX, y, pageWidth - marginX, y);
    y += 9;

    // ---- Title + status pill ----
    doc.setFont("helvetica", "bold").setFontSize(16).setTextColor(...DARK);
    doc.text("Transaction Details", marginX, y);
    const label = statusInfo.label || "-";
    doc.setFontSize(10).setFont("helvetica", "bold");
    const pillW = doc.getTextWidth(label) + 10;
    doc.setFillColor(...sc);
    doc.roundedRect(pageWidth - marginX - pillW, y - 5, pillW, 7, 1.5, 1.5, "F");
    doc.setTextColor(255, 255, 255);
    doc.text(label, pageWidth - marginX - pillW / 2, y, { align: "center" });
    y += 9;

    // ---- Meta line ----
    doc.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED);
    doc.text(`Order ID: ${statusDialog.orderId || "-"}`, marginX, y);
    let gen = "";
    try {
      gen = new Date().toLocaleString();
    } catch (e) {
      gen = "";
    }
    doc.text(`Generated: ${gen}`, pageWidth - marginX, y, { align: "right" });
    if (statusBody?.resultInfo?.resultMsg) {
      y += 5;
      doc.text(`Message: ${statusBody.resultInfo.resultMsg}`, marginX, y, {
        maxWidth: pageWidth - marginX * 2,
      });
    }
    y += 3;

    const pdfRows = buildReadableRows(statusBody);
    if (statusDialog.upiId) pdfRows.push(["UPI ID", `${statusDialog.upiId}`]);
    if (hasRefund) {
      pdfRows.push(["Refund Status", refundStatusLabel(refundInfo.status) || "-"]);
      pdfRows.push([
        "Refund Amount",
        refundInfo.amount != null ? formatAmount(refundInfo.amount) : "-",
      ]);
      pdfRows.push(["Refund Time", fmtDateTime(refundInfo.date)]);
      if (refundInfo.refId) pdfRows.push(["Refund Ref", `${refundInfo.refId}`]);
    }
    doc.autoTable({
      startY: y + 2,
      head: [["Field", "Value"]],
      body: pdfRows,
      theme: "grid",
      styles: { fontSize: 10, cellPadding: 3, textColor: DARK, lineColor: [229, 231, 235] },
      headStyles: { fillColor: PRIMARY, textColor: 255, fontStyle: "bold" },
      alternateRowStyles: { fillColor: [247, 249, 252] },
      columnStyles: { 0: { cellWidth: 62, fontStyle: "bold", textColor: MUTED } },
      margin: { left: marginX, right: marginX },
    });

    // ---- Footer on every page ----
    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p += 1) {
      doc.setPage(p);
      doc.setDrawColor(229, 231, 235).setLineWidth(0.3);
      doc.line(marginX, pageHeight - 16, pageWidth - marginX, pageHeight - 16);
      doc.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
      doc.text(
        "This is a system-generated document for your records. Contact: info@amiktech.in | www.amiktech.in",
        pageWidth / 2,
        pageHeight - 11,
        { align: "center" }
      );
      doc.text(`Page ${p} of ${pageCount}`, pageWidth / 2, pageHeight - 7, {
        align: "center",
      });
    }

    doc.save(`transaction_${statusDialog.orderId}.pdf`);
  };

  const applyFilters = () => setApplied(inputs);

  const handleExport = () => {
    if (!rows.length) return;
    const csv = changeToCSV({
      headers: columns.map((c) => c.headerName),
      properties: columns.map((c) => c.field),
      valuesObjArr: rows,
    });
    downloadCSV(csv, `gateway_${id}_transactions_${applied.from}_to_${applied.to}`);
  };

  const tableColumns = useMemo(() => {
    if (!canCheckLiveStatus) return columns;
    return [
      ...columns,
      {
        field: "liveStatus",
        headerName: "Live Status",
        minWidth: 130,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            disabled={!row.order_id}
            onClick={() => fetchStatus(row.order_id)}
          >
            View
          </Button>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCheckLiveStatus, id]);

  const readableRows = buildReadableRows(statusBody);

  return (
    <Box>
      <Card sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From"
              InputLabelProps={{ shrink: true }}
              value={inputs.from}
              onChange={(e) => setInputs({ ...inputs, from: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To"
              InputLabelProps={{ shrink: true }}
              value={inputs.to}
              onChange={(e) => setInputs({ ...inputs, to: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={6}>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={applyFilters} disabled={isFetching}>
                Apply
              </Button>
              <Button variant="outlined" onClick={() => refetch()} disabled={isFetching}>
                Refresh
              </Button>
              <Button
                variant="outlined"
                onClick={handleExport}
                disabled={!supported || rows.length === 0}
              >
                Export CSV
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Card>

      {!supported ? (
        <Alert severity="info">
          {data?.message ||
            `Transaction listing is not supported for this gateway provider (${
              data?.gateway_type || "unknown"
            }).`}
        </Alert>
      ) : isFetching && rows.length === 0 ? (
        <Loader />
      ) : (
        <Card sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6">
              Transactions
              <Chip size="small" label={`${rows.length}`} sx={{ ml: 1 }} />
            </Typography>
          </Stack>
          <BaseTable
            rows={rows}
            columns={tableColumns}
            isLoading={isFetching}
            getRowId={(row) => row.rowId}
            pageSize={25}
            rowsPerPageOptions={[25, 50, 100]}
            showExportCsvBtn={false}
            height={600}
          />
        </Card>
      )}

      {/* Readable live status + manual refund */}
      <Dialog
        open={statusDialog.open}
        onClose={() => setStatusDialog((s) => ({ ...s, open: false }))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Transaction Details
          <Typography variant="body2" color="text.secondary">
            {statusDialog.orderId}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {statusDialog.loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : statusDialog.error ? (
            <Alert severity="error">{statusDialog.error}</Alert>
          ) : statusBody ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip label={statusInfo.label} color={statusInfo.color} />
                {statusBody?.resultInfo?.resultMsg && (
                  <Typography variant="body2" color="text.secondary">
                    {statusBody.resultInfo.resultMsg}
                  </Typography>
                )}
              </Stack>

              <MuiTable size="small">
                <MuiTableBody>
                  {readableRows.map(([label, value]) => (
                    <MuiTableRow key={label}>
                      <MuiTableCell sx={{ fontWeight: 600, width: "45%", border: 0 }}>
                        {label}
                      </MuiTableCell>
                      <MuiTableCell sx={{ border: 0 }}>{value}</MuiTableCell>
                    </MuiTableRow>
                  ))}
                  {statusDialog.upiId && (
                    <MuiTableRow>
                      <MuiTableCell sx={{ fontWeight: 600, width: "45%", border: 0 }}>
                        UPI ID
                      </MuiTableCell>
                      <MuiTableCell sx={{ border: 0 }}>{statusDialog.upiId}</MuiTableCell>
                    </MuiTableRow>
                  )}
                </MuiTableBody>
              </MuiTable>

              {/* Refund status + time (when a refund is recorded). */}
              {hasRefund && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Refund
                  </Typography>
                  <MuiTable size="small">
                    <MuiTableBody>
                      <MuiTableRow>
                        <MuiTableCell sx={{ fontWeight: 600, width: "45%", border: 0 }}>
                          Refund Status
                        </MuiTableCell>
                        <MuiTableCell sx={{ border: 0 }}>
                          {refundStatusLabel(refundInfo.status) || "-"}
                        </MuiTableCell>
                      </MuiTableRow>
                      <MuiTableRow>
                        <MuiTableCell sx={{ fontWeight: 600, border: 0 }}>
                          Refund Amount
                        </MuiTableCell>
                        <MuiTableCell sx={{ border: 0 }}>
                          {refundInfo.amount != null ? formatAmount(refundInfo.amount) : "-"}
                        </MuiTableCell>
                      </MuiTableRow>
                      <MuiTableRow>
                        <MuiTableCell sx={{ fontWeight: 600, border: 0 }}>
                          Refund Time
                        </MuiTableCell>
                        <MuiTableCell sx={{ border: 0 }}>
                          {fmtDateTime(refundInfo.date)}
                        </MuiTableCell>
                      </MuiTableRow>
                      {refundInfo.refId && (
                        <MuiTableRow>
                          <MuiTableCell sx={{ fontWeight: 600, border: 0 }}>
                            Refund Ref
                          </MuiTableCell>
                          <MuiTableCell sx={{ border: 0 }}>{refundInfo.refId}</MuiTableCell>
                        </MuiTableRow>
                      )}
                    </MuiTableBody>
                  </MuiTable>
                </Box>
              )}

              {/* Manual refund — only when nothing has been refunded yet. */}
              {canRefund && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Trigger Refund
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    No refund recorded for this transaction. Refund amount cannot exceed{" "}
                    {formatAmount(txnAmount)}.
                  </Typography>
                  <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Refund amount"
                        value={refund.amount}
                        onChange={(e) => setRefund((r) => ({ ...r, amount: e.target.value }))}
                        inputProps={{ min: 0, max: txnAmount ?? undefined, step: "0.01" }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Reason (optional)"
                        value={refund.reason}
                        onChange={(e) => setRefund((r) => ({ ...r, reason: e.target.value }))}
                      />
                    </Grid>
                  </Grid>
                  {refund.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {refund.error}
                    </Alert>
                  )}
                  {refund.message && (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      {refund.message}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color="warning"
                    sx={{ mt: 1.5 }}
                    onClick={handleRefund}
                    disabled={refund.submitting}
                  >
                    {refund.submitting ? "Processing..." : "Trigger Refund"}
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            <Typography variant="body2">No status returned.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDownloadPdf} disabled={!statusBody}>
            Download PDF
          </Button>
          <Button onClick={() => setStatusDialog((s) => ({ ...s, open: false }))}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentGatewayTransactions;
